import React, { useState } from 'react';
import { Card, Button, Space, Typography, Select, Input, DatePicker, InputNumber, Form, Modal } from 'antd';
import { PlusOutlined, FilterOutlined, FunctionOutlined } from '@ant-design/icons';
import { useDrop } from 'react-dnd';
import moment from 'moment';

const { Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const FilterBuilder = ({ onAddFilter, onAddCondition, availableFields = [] }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [filterType, setFilterType] = useState('condition'); // 'condition' or 'formula'
  const [selectedField, setSelectedField] = useState(null);
  const [form] = Form.useForm();

  const [{ isOver }, drop] = useDrop(() => ({
    accept: 'field',
    drop: (item) => {
      // Auto-create filter when field is dropped
      const newFilter = {
        field: item.field,
        operator: getDefaultOperator(item.field.type),
        value: undefined,
        valueTo: undefined,
        type: item.field.type
      };
      onAddFilter && onAddFilter(newFilter);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  }));

  const getDefaultOperator = (fieldType) => {
    switch (fieldType) {
      case 'string':
        return 'contains';
      case 'number':
        return 'equals';
      case 'date':
        return 'equals';
      case 'boolean':
        return 'equals';
      default:
        return 'equals';
    }
  };

  const operators = {
    string: [
      { value: 'equals', label: 'Equals' },
      { value: 'notEquals', label: 'Not Equals' },
      { value: 'contains', label: 'Contains' },
      { value: 'startsWith', label: 'Starts With' },
      { value: 'endsWith', label: 'Ends With' },
      { value: 'isNull', label: 'Is Empty' },
      { value: 'isNotNull', label: 'Is Not Empty' }
    ],
    number: [
      { value: 'equals', label: 'Equals' },
      { value: 'notEquals', label: 'Not Equals' },
      { value: 'greaterThan', label: 'Greater Than' },
      { value: 'greaterThanOrEqual', label: 'Greater Than or Equal' },
      { value: 'lessThan', label: 'Less Than' },
      { value: 'lessThanOrEqual', label: 'Less Than or Equal' },
      { value: 'between', label: 'Between' },
      { value: 'isNull', label: 'Is Empty' },
      { value: 'isNotNull', label: 'Is Not Empty' }
    ],
    date: [
      { value: 'equals', label: 'Equals' },
      { value: 'notEquals', label: 'Not Equals' },
      { value: 'greaterThan', label: 'After' },
      { value: 'greaterThanOrEqual', label: 'On or After' },
      { value: 'lessThan', label: 'Before' },
      { value: 'lessThanOrEqual', label: 'On or Before' },
      { value: 'between', label: 'Between' },
      { value: 'isNull', label: 'Is Empty' },
      { value: 'isNotNull', label: 'Is Not Empty' }
    ],
    boolean: [
      { value: 'equals', label: 'Is' },
      { value: 'notEquals', label: 'Is Not' }
    ]
  };

  const renderValueInput = (fieldType, operator) => {
    switch (fieldType) {
      case 'string':
        return <Input placeholder="Enter value" />;
      case 'number':
        if (operator === 'min' || operator === 'max') {
          return <InputNumber placeholder={operator === 'min' ? 'Min value' : 'Max value'} style={{ width: '120px' }} />;
        }
        return <InputNumber placeholder="Enter number" style={{ width: '100%' }} />;
      case 'date':
        if (operator === 'min' || operator === 'max') {
          return <DatePicker placeholder={operator === 'min' ? 'Start date' : 'End date'} style={{ width: '140px' }} />;
        }
        return <DatePicker placeholder="Select date" style={{ width: '100%' }} />;
      case 'boolean':
        return (
          <Select placeholder="Select value" style={{ width: '100%' }}>
            <Option value={true}>True</Option>
            <Option value={false}>False</Option>
          </Select>
        );
      default:
        return <Input placeholder="Enter value" />;
    }
  };

  const handleFieldChange = (fieldName) => {
    const field = availableFields.find(f => f.name === fieldName);
    setSelectedField(field);
    // Reset operator and value when field changes
    form.setFieldsValue({ operator: undefined, value: undefined, valueTo: undefined });
  };

  const handleAddFilter = (values) => {
    if (!selectedField) return;

    const newFilter = {
      field: selectedField,
      operator: values.operator,
      value: values.value,
      valueTo: values.valueTo,
      type: selectedField.type
    };

    onAddFilter && onAddFilter(newFilter);
    setIsModalVisible(false);
    setSelectedField(null);
    form.resetFields();
  };

  const handleAddFormula = (values) => {
    // Handle formula conditions
    const formulaCondition = {
      formula: values.formula,
      operator: values.operator,
      value: values.value,
      type: 'formula'
    };

    onAddCondition && onAddCondition(formulaCondition);
    setIsModalVisible(false);
    form.resetFields();
  };

  return (
    <>
      <Card
        title={
          <Space>
            <FilterOutlined />
            Filter Builder
          </Space>
        }
        size="small"
        style={{
          height: '100%',
          border: isOver ? '2px dashed #1890ff' : '1px solid #d9d9d9',
          backgroundColor: isOver ? '#f0f8ff' : '#fff'
        }}
        bodyStyle={{ height: 'calc(100% - 60px)', overflow: 'auto', padding: '16px' }}
      >
        <div
          ref={drop}
          style={{
            minHeight: '200px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '16px'
          }}
        >
          <div style={{ textAlign: 'center', color: '#999' }}>
            <FilterOutlined style={{ fontSize: '24px', marginBottom: '8px' }} />
            <br />
            Drag fields here to create filters
            <br />
            <small>or use the buttons below</small>
          </div>

          <Space direction="vertical">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setFilterType('condition');
                setIsModalVisible(true);
              }}
            >
              Add Condition
            </Button>
            <Button
              icon={<FunctionOutlined />}
              onClick={() => {
                setFilterType('formula');
                setIsModalVisible(true);
              }}
            >
              Add Formula
            </Button>
          </Space>
        </div>
      </Card>

      {/* Filter Modal */}
      <Modal
        title={filterType === 'condition' ? 'Add Filter Condition' : 'Add Formula Condition'}
        visible={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setSelectedField(null);
          form.resetFields();
        }}
        footer={null}
        width={600}
        zIndex={1100}
        style={{ top: 20 }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={filterType === 'condition' ? handleAddFilter : handleAddFormula}
        >
          {filterType === 'condition' ? (
            <>
              <Form.Item
                name="fieldName"
                label="Field"
                rules={[{ required: true, message: 'Please select a field' }]}
              >
                <Select placeholder="Select field" onChange={handleFieldChange}>
                  {availableFields.map(field => (
                    <Option key={field.name} value={field.name}>
                      {field.label} ({field.type})
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="operator"
                label="Operator"
                rules={[{ required: true, message: 'Please select an operator' }]}
              >
                <Select placeholder="Select operator">
                  {selectedField && (selectedField.operators || operators[selectedField.type] || operators.string)?.map(op => {
                    // Handle both string arrays from backend and object arrays from frontend
                    if (typeof op === 'string') {
                      return (
                        <Option key={op} value={op}>
                          {op.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                        </Option>
                      );
                    } else {
                      return (
                        <Option key={op.value} value={op.value}>
                          {op.label}
                        </Option>
                      );
                    }
                  })}
                </Select>
              </Form.Item>

              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues.fieldName !== currentValues.fieldName ||
                  prevValues.operator !== currentValues.operator
                }
              >
                {({ getFieldValue }) => {
                  const fieldName = getFieldValue('fieldName');
                  const operator = getFieldValue('operator');
                  const field = availableFields.find(f => f.name === fieldName);

                  if (!field || !operator || operator === 'isNull' || operator === 'isNotNull') {
                    return null;
                  }

                  if (operator === 'between') {
                    return (
                      <Form.Item label="Value Range" required>
                        <Form.Item name="value" noStyle rules={[{ required: true, message: 'Start value required' }]}>
                          {renderValueInput(field.type, 'min')}
                        </Form.Item>
                        <span style={{ margin: '0 8px' }}>to</span>
                        <Form.Item name="valueTo" noStyle rules={[{ required: true, message: 'End value required' }]}>
                          {renderValueInput(field.type, 'max')}
                        </Form.Item>
                      </Form.Item>
                    );
                  }

                  return (
                    <Form.Item name="value" label="Value" rules={[{ required: true, message: 'Please enter a value' }]}>
                      {renderValueInput(field.type, operator)}
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </>
          ) : (
            <>
              <Form.Item
                name="formula"
                label="Formula"
                rules={[{ required: true, message: 'Please enter a formula' }]}
              >
                <Input.TextArea
                  placeholder="Enter formula (e.g., total_sale - sales_tax)"
                  rows={3}
                />
              </Form.Item>

              <Form.Item
                name="operator"
                label="Comparison"
                rules={[{ required: true, message: 'Please select comparison operator' }]}
              >
                <Select placeholder="Select comparison">
                  <Option value="equals">=</Option>
                  <Option value="notEquals">≠</Option>
                  <Option value="greaterThan">Greater Than</Option>
                  <Option value="lessThan">Less Than</Option>
                  <Option value="contains">Contains</Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="value"
                label="Value"
                rules={[{ required: true, message: 'Please enter comparison value' }]}
              >
                <Input placeholder="Enter value to compare against" />
              </Form.Item>
            </>
          )}

          <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
            <Space>
              <Button onClick={() => {
                setIsModalVisible(false);
                setSelectedField(null);
                form.resetFields();
              }}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Add {filterType === 'condition' ? 'Condition' : 'Formula'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default FilterBuilder;